import { useState } from 'react';
import useFetchDataRoomFiles from '../../../../../DataRoom/useFetchDataRoomFiles';
import { generateCreditAnalysisReport } from '../../../../../../store/dashboard/creditBureau/apiHelper';
import {
    CREDIT_ANALYSIS_COMBINED_FALLBACK_NAME,
    CREDIT_ANALYSIS_FALLBACK_NAME,
    CREDIT_ANALYSIS_SCOPE,
} from '../../../../../../store/dashboard/creditBureau/constants';
import { CreditAnalysisReportDoc, CreditAnalysisScope } from '../../../../../../store/dashboard/creditBureau/types';

/**
 * Generates the Credit Analysis workbook and opens it in the shared dataroom viewer.
 *
 * Mirrors useGstAnalysisReport: the same generate → open → download shape, the
 * same reuse of useFetchDataRoomFiles for the presigned-URL fetch, and the same
 * split of the request into the store's service/apiHelper layer — so the two
 * report buttons behave identically for the user.
 */
export const useCreditAnalysisReport = () => {
    const [generatingReport, setGeneratingReport] = useState(false);
    const [reportDoc, setReportDoc] = useState<CreditAnalysisReportDoc | null>(null);
    const { handleFileClick, showFile, setShowFile, fileUrl, fileMimeType, downloadTheFile, downloadingFile } = useFetchDataRoomFiles();

    // searchParams.get() yields string | null, so accept null rather than making
    // every caller coalesce. The guard below narrows all three to string.
    const openReport = async (
        investeeOrgId?: string | null,
        directorId?: string | null,
        bureau?: string | null,
        scope: CreditAnalysisScope = CREDIT_ANALYSIS_SCOPE.INDIVIDUAL,
    ) => {
        // A combined report needs no directorId; an individual one does.
        const needsDirector = scope === CREDIT_ANALYSIS_SCOPE.INDIVIDUAL;
        if (!investeeOrgId || !bureau || (needsDirector && !directorId) || generatingReport) return;
        setGeneratingReport(true);
        try {
            const report = await generateCreditAnalysisReport({
                investeeOrgId,
                bureau,
                scope,
                // COMBINED carries no directorId — the Lambda discovers every
                // director of the org itself.
                ...(needsDirector ? { directorId: directorId! } : {}),
            });
            if (report?.mnemosyneDocId) {
                const doc: CreditAnalysisReportDoc = {
                    docId: report.mnemosyneDocId,
                    name:
                        report.fileName ??
                        (scope === CREDIT_ANALYSIS_SCOPE.COMBINED ? CREDIT_ANALYSIS_COMBINED_FALLBACK_NAME : CREDIT_ANALYSIS_FALLBACK_NAME),
                };
                setReportDoc(doc);
                await handleFileClick(doc.docId, doc.name);
            }
        } finally {
            // Always clear the spinner, even if handleFileClick rejects.
            setGeneratingReport(false);
        }
    };

    const closeReport = () => setShowFile(false);

    const downloadReport = () => {
        if (reportDoc?.docId) downloadTheFile(reportDoc.docId, reportDoc.name);
    };

    return { generatingReport, reportDoc, showFile, fileUrl, fileMimeType, downloadingFile, openReport, closeReport, downloadReport };
};
